<template>
    <form-wrapper @form-submit="submit">
        <form-row>
            <form-cell width="half">
                <text-input
                    :formData="data"
                    :label="$t('forms.company.create_company.fields.name.label')"
                    :placeholder="$t('forms.company.create_company.fields.name.placeholder')"
                    :isRequired="true"
                    :isDisabled="isDisabled"
                    type="text"
                    valuePath="name"
                    @change="change"
                ></text-input>
            </form-cell>

            <form-cell width="half">
                <text-input
                    :formData="data"
                    :label="$t('forms.company.create_company.fields.pib.label')"
                    :placeholder="$t('forms.company.create_company.fields.pib.placeholder')"
                    :isRequired="true"
                    :isDisabled="isDisabled"
                    type="text"
                    valuePath="pib"
                    @change="change"
                ></text-input>
            </form-cell>
        </form-row>

        <form-row>
            <form-cell width="half">
                <text-input
                    :formData="data"
                    :label="$t('forms.company.create_company.fields.address.label')"
                    :placeholder="$t('forms.company.create_company.fields.address.placeholder')"
                    :isRequired="true"
                    :isDisabled="isDisabled"
                    type="text"
                    valuePath="address"
                    @change="change"
                ></text-input>
            </form-cell>
            <form-cell width="half">
                <select-input
                    ref="citySelect"
                    :formData="data"
                    :label="$t('forms.company.create_company.fields.city.label')"
                    :placeholder="$t('forms.company.create_company.fields.city.placeholder')"
                    :isRequired="true"
                    :isDisabled="isDisabled"
                    :options="cityOptions"
                    valuePath="cityId"
                    :isSearchable="true"
                    @change="change"
                    @clear="clear"
                ></select-input>
            </form-cell>
        </form-row>

        <form-row>
            <form-cell width="half">
                <text-input
                    :formData="data"
                    :label="$t('forms.user.profile.fields.facebook.label')"
                    :placeholder="$t('forms.user.profile.fields.facebook.placeholder')"
                    :isDisabled="isDisabled"
                    type="text"
                    valuePath="facebook"
                    @change="change"
                ></text-input>
            </form-cell>

            <form-cell width="half">
                <text-input
                    :formData="data"
                    :label="$t('forms.user.profile.fields.instagram.label')"
                    :placeholder="$t('forms.user.profile.fields.instagram.placeholder')"
                    :isDisabled="isDisabled"
                    type="text"
                    valuePath="instagram"
                    @change="change"
                ></text-input>
            </form-cell>
        </form-row>

        <form-row>
            <form-cell width="half">
                <text-input
                    :formData="data"
                    :label="$t('forms.user.profile.fields.linkedin.label')"
                    :placeholder="$t('forms.user.profile.fields.linkedin.placeholder')"
                    :isDisabled="isDisabled"
                    type="text"
                    valuePath="linkedIn"
                    @change="change"
                ></text-input>
            </form-cell>

            <form-cell width="half">
                <text-input
                    :formData="data"
                    :label="$t('forms.user.profile.fields.twitter.label')"
                    :placeholder="$t('forms.user.profile.fields.twitter.placeholder')"
                    :isDisabled="isDisabled"
                    type="text"
                    valuePath="twitter"
                    @change="change"
                ></text-input>
            </form-cell>
        </form-row>
        <form-row>
            <form-cell width="half">
                <text-input
                    :formData="data"
                    :label="$t('forms.user.profile.fields.tiktok.label')"
                    :placeholder="$t('forms.user.profile.fields.tiktok.placeholder')"
                    :isDisabled="isDisabled"
                    type="text"
                    valuePath="tiktok"
                    @change="change"
                ></text-input>
            </form-cell>
        </form-row>
        <form-row>
            <form-cell>
                <textarea-input
                    :formData="data"
                    :label="$t('forms.offer.create_offer.fields.description.label')"
                    :placeholder="$t('forms.user.profile.fields.description.placeholder')"
                    :isDisabled="isDisabled"
                    :maxlength="600"
                    valuePath="description"
                    @change="change"
                ></textarea-input>
            </form-cell>
        </form-row>

        <template #footer>
            <common-buttons v-if="!isUser">
                <common-button
                    type="primary"
                    :isProcessing="isLoading"
                    :label="$t('forms.user.profile.buttons.save')"
                    :isDisabled="isDisabled"
                ></common-button>
            </common-buttons>
        </template>
    </form-wrapper>
</template>

<script>
    import Store from '~/store'
    import FormMixin from 'mixins/FormMixin'
    import CompanyAPI from 'api/CompanyAPI'
    import SocialMediaEnums from 'enums/social_media.enum'
    import { getSocialMediaURL } from '~/helpers'

    export default {
        mixins: [FormMixin],

        props: {
            company: { type: Object, required: true },
            isUser: { type: Boolean, default: false },
        },

        data() {
            return {
                cityOptions: [],
            }
        },

        computed: {
            isDisabled() {
                return this.isUser || !!this.company.deletedAt
            },
        },

        methods: {
            submit() {
                const socialMediaPaths = {}
                getSocialMediaURL(this.data, socialMediaPaths, SocialMediaEnums)

                const updatedCompanyInfo = {
                    name: this.data.name,
                    pib: this.data.pib,
                    address: this.data.address,
                    cityId: this.data.cityId,
                    description: this.data.description ?? null,
                    subdomain: this.data.subdomain ?? null,
                    theme: this.data.theme ?? null,
                    yearlySubscription: this.company.yearlySubscription,
                    ...socialMediaPaths,
                }
                this.submitForm(CompanyAPI.updateCompany(updatedCompanyInfo, this.company.id))
                    .then(() => {
                        this.$toast.success(this.$t('common.microcopy.success'))
                        this.emitEvent({ name: 'reload-company' })
                        this.$auth.reloadUser()
                    })
                    .catch(() => {})
            },

            fetchCities(company) {
                this.cityOptions = Store.getters['data/getCountries']
                    .find((country) => country.name === company.city?.country?.name)
                    .cities.map((city) => ({
                        value: city.id,
                        label: city.name,
                    }))
            },
        },

        watch: {
            company: {
                handler: function () {
                    this.$refs.citySelect.searchTerm = this.company.city.name
                    this.fetchCities(this.company)
                    this.prefillData(this.company)
                },
            },
        },

        mounted() {
            this.prefillData(this.company)
        },

        created() {
            this.fetchCities(this.company)
        },
    }
</script>
